import { Form, FormSection } from '@components/forms';
import { UnderwritingActivityItem } from '@models/underwritingReferral';
import { getUnderwritingActivityFormField } from './getUnderwritingActivityFormField';

import { useSearchUnderwritingActivityType } from '@services/underwritingReferral/useSearchUnderwritingActivityType';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { Box } from '@mui/material';

export type TUnderwritingActivityFormValues = {
  resolve: boolean;
  resolutionNote?: string;
  resolutionReasonId?: string;
  description?: string;
  type?: string;
};

export const UnderwritingActivityForm = ({
  activity,
  onChange,
}: {
  activity?: UnderwritingActivityItem;
  onChange: Dispatch<
    SetStateAction<{
      clearActivity?: string;
      resolutionNote?: string;
      resolutionReasonId?: string;
      description?: string;
      type?: string;
    }>
  >;
}) => {
  const isEditing = Boolean(activity);
  const { underwritingActivityTypes } = useSearchUnderwritingActivityType();

  const methods = useForm<TUnderwritingActivityFormValues>({
    defaultValues: {
      resolve: false,
      resolutionNote: activity?.note || '',
      resolutionReasonId: undefined,
      description: activity?.description || '',
      type: activity?.underwritingActivityType?.id || '',
    },
  });

  const formValues = methods.watch();

  const uwActivityFormFields = getUnderwritingActivityFormField({
    isEditing,
    underwritingActivityTypes:
      underwritingActivityTypes?.map((type) => ({
        label: type.label,
        value: type.id,
      })) || [],
    resolutionReasons:
      activity?.underwritingActivityType?.availableResolutionReasons?.map((reason) => ({
        label: reason?.underwritingActivityResolutionReason?.label,
        value: reason?.underwritingActivityResolutionReason?.id,
      })) || [],
  });
  useEffect(() => {
    onChange(
      isEditing
        ? {
            clearActivity: formValues.resolve ? 'yes' : 'no',
            resolutionNote: formValues.resolutionNote,
            resolutionReasonId: formValues.resolve ? formValues.resolutionReasonId : undefined,
          }
        : {
            description: formValues.description,
            type: formValues.type,
          },
    );
  }, [formValues, onChange, isEditing]);

  return (
    <Form formId='create-underwriting-activity' methods={methods}>
      <Box
        sx={{
          minHeight: 350,
        }}
      >
        <FormSection fields={uwActivityFormFields} />
      </Box>
    </Form>
  );
};

export const UnderwritingActivityFormWithMethods = ({
  activity,
  methods,
}: {
  activity?: UnderwritingActivityItem;
  methods: UseFormReturn<TUnderwritingActivityFormValues, any>;
}) => {
  const isEditing = Boolean(activity);
  const { underwritingActivityTypes } = useSearchUnderwritingActivityType();

  useEffect(() => {
    methods.reset({
      resolve: activity?.resolvedAt ? true : false,
      resolutionNote: activity?.note ?? undefined,
      resolutionReasonId: undefined,
    });
  }, [activity, methods]);

  const uwActivityFormFields = getUnderwritingActivityFormField({
    isEditing,
    underwritingActivityTypes:
      underwritingActivityTypes?.map((type) => ({
        label: type.label,
        value: type.id,
      })) || [],
    resolutionReasons:
      activity?.underwritingActivityType?.availableResolutionReasons?.map((reason) => ({
        label: reason?.underwritingActivityResolutionReason?.label,
        value: reason?.underwritingActivityResolutionReason?.id,
      })) || [],
  });

  return (
    <Form formId='create-underwriting-activity' methods={methods}>
      <FormSection fields={uwActivityFormFields} />
    </Form>
  );
};
